import { useState, useEffect } from "react";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import toast from "react-hot-toast";
import moment from "moment";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Box,
  Card,
  CardContent,
  Grid,
  Avatar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ReceiptIcon from "@mui/icons-material/Receipt";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import * as XLSX from "xlsx";

const RelatorioContabilidadeAnalitica = ({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
}) => {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();
  const [dadosRelatorio, setDadosRelatorio] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  useEffect(() => {
    if (
      acao &&
      dataFiltro.dataInicial &&
      dataFiltro.dataFinal &&
      atualizacoes > 0
    ) {
      buscarDadosRelatorio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acao, dataFiltro, atualizacoes]);

  const buscarDadosRelatorio = async () => {
    startLoading();
    try {
      const dataInicio = moment(dataFiltro.dataInicial).format("YYYY-MM-DD");
      const dataFim = moment(dataFiltro.dataFinal).format("YYYY-MM-DD");

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/getContabilidadeAnalitica/${dadosClinica._id}`,
        "POST",
        JSON.stringify({
          dataInicio,
          dataFim,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      setDadosRelatorio(responseData);

      if (acao === "exportar") {
        exportarParaExcel(responseData);
      }

      toast.success("Relatório gerado com sucesso!");
    } catch (err) {
      console.error("Erro ao buscar dados do relatório:", err);
      toast.error(
        "Erro ao gerar relatório: " + (err.message || "Erro desconhecido"),
      );
    } finally {
      stopLoading();
    }
  };

  const formatarValor = (valor) => {
    return new Intl.NumberFormat("pt-PT").format(valor || 0) + " CVE";
  };

  const corSaldo = (saldo) => {
    if (saldo > 0) return "success.main";
    if (saldo < 0) return "error.main";
    return "text.secondary";
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // Extrai detalhes específicos de um dentista a partir do bloco "detalhes" global
  const detalhesDoDentista = (dentistaId) => {
    if (!dadosRelatorio?.detalhes) {
      return {
        procedimentosPlano: [],
        tratamentosIsolados: [],
        despesas: [],
        consumoStock: [],
      };
    }
    const id = dentistaId.toString();
    return {
      procedimentosPlano: dadosRelatorio.detalhes.procedimentosPlano.filter(
        (d) => d.dentista?.toString() === id,
      ),
      tratamentosIsolados: dadosRelatorio.detalhes.tratamentosIsolados.filter(
        (d) => d.dentista?.toString() === id,
      ),
      despesas: dadosRelatorio.detalhes.despesas.filter(
        (d) => d.dentista?.toString() === id,
      ),
      consumoStock: dadosRelatorio.detalhes.consumoStock.filter(
        (d) => d.dentista?.toString() === id,
      ),
    };
  };

  const exportarParaExcel = (dados) => {
    try {
      const workbook = XLSX.utils.book_new();

      // ============================================
      // Folha 1: Resumo por dentista
      // ============================================
      const resumoData = [];

      resumoData.push([`Contabilidade Analítica — ${dadosClinica.nome}`]);
      resumoData.push([
        `Período: ${moment(dados.periodo.inicio).format(
          "DD/MM/YYYY",
        )} a ${moment(dados.periodo.fim).format("DD/MM/YYYY")}`,
      ]);
      resumoData.push([]);

      resumoData.push([
        "Dentista",
        "Email",
        "Estado",
        "Valor Procedimentos (Plano)",
        "Sessões (Plano)",
        "Procedimentos (Plano)",
        "Valor Tratamentos Isolados",
        "Sessões (Isolados)",
        "Procedimentos (Isolados)",
        "Total Entradas",
        "Valor Despesas",
        "Nº Despesas",
        "Valor Consumo Stock",
        "Nº Movimentos Stock",
        "Total Gastos",
        "Saldo",
      ]);

      dados.dentistas.forEach((linha) => {
        resumoData.push([
          linha.dentista.name,
          linha.dentista.email || "",
          linha.dentista.ativo ? "Ativo" : "Inativo",
          linha.entradas.procedimentosPlano.valor,
          linha.entradas.procedimentosPlano.numSessoes,
          linha.entradas.procedimentosPlano.numProcedimentos,
          linha.entradas.tratamentosIsolados.valor,
          linha.entradas.tratamentosIsolados.numSessoes,
          linha.entradas.tratamentosIsolados.numProcedimentos,
          linha.entradas.total,
          linha.gastos.despesas.valor,
          linha.gastos.despesas.numItens,
          linha.gastos.consumoStock.valor,
          linha.gastos.consumoStock.numMovimentos,
          linha.gastos.total,
          linha.saldo,
        ]);
      });

      // Linha de totais
      resumoData.push([]);
      resumoData.push([
        "TOTAIS",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        dados.totais.entradas,
        "",
        "",
        "",
        "",
        dados.totais.gastos,
        dados.totais.saldo,
      ]);

      const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
      wsResumo["!cols"] = [
        { wch: 25 },
        { wch: 25 },
        { wch: 10 },
        { wch: 18 },
        { wch: 12 },
        { wch: 14 },
        { wch: 18 },
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 12 },
        { wch: 16 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
      ];
      XLSX.utils.book_append_sheet(workbook, wsResumo, "Resumo");

      // ============================================
      // Folha 2: Detalhe procedimentos plano
      // ============================================
      const detPlanos = [
        [
          "Dentista",
          "Cliente",
          "Tratamento",
          "Dente",
          "Faces",
          "Data Sessão",
          "Total Sessões Procedimento",
          "Preço Segurado",
          "Valor Atribuído",
        ],
      ];
      const dentistasMap = new Map(
        dados.dentistas.map((l) => [
          l.dentista._id.toString(),
          l.dentista.name,
        ]),
      );
      (dados.detalhes.procedimentosPlano || []).forEach((d) => {
        detPlanos.push([
          dentistasMap.get(d.dentista?.toString()) || d.dentista,
          d.cliente,
          d.tratamento,
          d.dente || "",
          (d.faces || []).join(", "),
          moment(d.dataSessao).format("DD/MM/YYYY"),
          d.totalSessoes,
          d.precoSegurado,
          d.valor,
        ]);
      });
      const wsPlanos = XLSX.utils.aoa_to_sheet(detPlanos);
      XLSX.utils.book_append_sheet(workbook, wsPlanos, "Procedimentos Plano");

      // ============================================
      // Folha 3: Detalhe tratamentos isolados
      // ============================================
      const detIsolados = [
        [
          "Dentista",
          "Cliente",
          "Tratamento",
          "Dente",
          "Faces",
          "Data Sessão",
          "Total Sessões Grupo",
          "Sessão de Grupo",
          "Preço Particular",
          "Valor Atribuído",
        ],
      ];
      (dados.detalhes.tratamentosIsolados || []).forEach((d) => {
        detIsolados.push([
          dentistasMap.get(d.dentista?.toString()) || d.dentista,
          d.cliente,
          d.tratamento,
          d.dente || "",
          (d.faces || []).join(", "),
          moment(d.dataSessao).format("DD/MM/YYYY"),
          d.totalSessoesGrupo,
          d.ehGrupoSessao ? "Sim" : "Não",
          d.precoParticular,
          d.valor,
        ]);
      });
      const wsIsolados = XLSX.utils.aoa_to_sheet(detIsolados);
      XLSX.utils.book_append_sheet(
        workbook,
        wsIsolados,
        "Tratamentos Isolados",
      );

      // ============================================
      // Folha 4: Detalhe despesas
      // ============================================
      const detDespesas = [
        [
          "Dentista",
          "Descrição",
          "Observações",
          "Valor",
          "Data Pagamento",
          "Método",
          "Nr. Ordem",
        ],
      ];
      (dados.detalhes.despesas || []).forEach((d) => {
        detDespesas.push([
          dentistasMap.get(d.dentista?.toString()) || d.dentista,
          d.descricao || "",
          d.observacoes || "",
          d.valor,
          d.dataPagamento ? moment(d.dataPagamento).format("DD/MM/YYYY") : "",
          d.metodoPagamento || "",
          d.nrOrdemPagamento || "",
        ]);
      });
      const wsDespesas = XLSX.utils.aoa_to_sheet(detDespesas);
      XLSX.utils.book_append_sheet(workbook, wsDespesas, "Despesas");

      // ============================================
      // Folha 5: Detalhe consumo stock
      // ============================================
      const detStock = [
        [
          "Dentista",
          "Produto",
          "Lote",
          "Quantidade",
          "Preço Compra (Unidade)",
          "Custo Total",
          "Data",
          "Cliente",
        ],
      ];
      (dados.detalhes.consumoStock || []).forEach((d) => {
        detStock.push([
          dentistasMap.get(d.dentista?.toString()) || d.dentista,
          d.produto,
          d.lote || "",
          d.quantidade,
          d.precoCompra,
          d.valor,
          d.data ? moment(d.data).format("DD/MM/YYYY") : "",
          d.cliente || "",
        ]);
      });
      const wsStock = XLSX.utils.aoa_to_sheet(detStock);
      XLSX.utils.book_append_sheet(workbook, wsStock, "Consumo Stock");

      // ============================================
      // Guardar
      // ============================================
      const fileName = `Contabilidade_Analitica_${moment(
        dados.periodo.inicio,
      ).format("DD-MM-YYYY")}_${moment(dados.periodo.fim).format(
        "DD-MM-YYYY",
      )}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success("Arquivo Excel exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar para Excel:", error);
      toast.error("Erro ao exportar arquivo Excel");
    }
  };

  if (!dadosRelatorio) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h6" color="textSecondary">
          Selecione um período e clique em "Gerar Relatório" para visualizar os
          dados.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header do Relatório */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AccountBalanceIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="h5" component="h2">
              Contabilidade Analítica
            </Typography>
          </Box>
          <Typography variant="subtitle1" color="textSecondary">
            Clínica: {dadosClinica.nome} | Período:{" "}
            {moment(dadosRelatorio.periodo.inicio).format("DD/MM/YYYY")} a{" "}
            {moment(dadosRelatorio.periodo.fim).format("DD/MM/YYYY")}
          </Typography>
        </CardContent>
      </Card>

      {/* Cards de totais globais */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: "4px solid #4caf50" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TrendingUpIcon sx={{ mr: 1, color: "success.main" }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {formatarValor(dadosRelatorio.totais.entradas)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Entradas
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: "4px solid #f44336" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TrendingDownIcon sx={{ mr: 1, color: "error.main" }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {formatarValor(dadosRelatorio.totais.gastos)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Gastos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              borderLeft: `4px solid ${
                dadosRelatorio.totais.saldo >= 0 ? "#1976d2" : "#f44336"
              }`,
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AccountBalanceIcon
                  sx={{
                    mr: 1,
                    color: corSaldo(dadosRelatorio.totais.saldo),
                  }}
                />
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ color: corSaldo(dadosRelatorio.totais.saldo) }}
                  >
                    {formatarValor(dadosRelatorio.totais.saldo)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Saldo
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela resumo por dentista */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Resumo por Dentista ({dadosRelatorio.dentistas.length})
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.100" }}>
              <TableCell>
                <strong>Dentista</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Proc. Plano</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Trat. Isolados</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Total Entradas</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Despesas</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Consumo Stock</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Total Gastos</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Saldo</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dadosRelatorio.dentistas.map((linha) => (
              <TableRow key={linha.dentista._id} hover>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      src={linha.dentista.image}
                      sx={{ width: 28, height: 28 }}
                    >
                      {linha.dentista.name?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {linha.dentista.name}
                      </Typography>
                      {!linha.dentista.ativo && (
                        <Chip
                          label="Inativo"
                          size="small"
                          color="default"
                          sx={{ height: 16, fontSize: 10 }}
                        />
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {formatarValor(linha.entradas.procedimentosPlano.valor)}
                </TableCell>
                <TableCell align="right">
                  {formatarValor(linha.entradas.tratamentosIsolados.valor)}
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold" color="success.main">
                    {formatarValor(linha.entradas.total)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {formatarValor(linha.gastos.despesas.valor)}
                </TableCell>
                <TableCell align="right">
                  {formatarValor(linha.gastos.consumoStock.valor)}
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold" color="error.main">
                    {formatarValor(linha.gastos.total)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    fontWeight="bold"
                    sx={{ color: corSaldo(linha.saldo) }}
                  >
                    {formatarValor(linha.saldo)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {/* Linha de totais */}
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell>
                <strong>TOTAL</strong>
              </TableCell>
              <TableCell colSpan={2} />
              <TableCell align="right">
                <strong>{formatarValor(dadosRelatorio.totais.entradas)}</strong>
              </TableCell>
              <TableCell colSpan={2} />
              <TableCell align="right">
                <strong>{formatarValor(dadosRelatorio.totais.gastos)}</strong>
              </TableCell>
              <TableCell align="right">
                <Typography
                  fontWeight="bold"
                  sx={{ color: corSaldo(dadosRelatorio.totais.saldo) }}
                >
                  {formatarValor(dadosRelatorio.totais.saldo)}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Drill-down por dentista */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Detalhes por Dentista
      </Typography>

      {dadosRelatorio.dentistas.length === 0 ? (
        <Card>
          <CardContent>
            <Typography
              variant="body1"
              color="textSecondary"
              textAlign="center"
            >
              Nenhum dentista no período selecionado.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        dadosRelatorio.dentistas.map((linha, index) => {
          const det = detalhesDoDentista(linha.dentista._id);
          const semMovimento =
            linha.entradas.total === 0 && linha.gastos.total === 0;

          return (
            <Accordion
              key={linha.dentista._id}
              expanded={expandedAccordion === `dentista-${index}`}
              onChange={handleAccordionChange(`dentista-${index}`)}
              sx={{ mb: 1 }}
              disabled={semMovimento}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Avatar
                    src={linha.dentista.image}
                    sx={{ mr: 2, width: 36, height: 36 }}
                  >
                    {linha.dentista.name?.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="h6">
                        {linha.dentista.name}
                      </Typography>
                      {!linha.dentista.ativo && (
                        <Chip label="Inativo" size="small" />
                      )}
                      {semMovimento && (
                        <Chip
                          label="Sem movimento no período"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Entradas: {formatarValor(linha.entradas.total)} | Gastos:{" "}
                      {formatarValor(linha.gastos.total)} | Saldo:{" "}
                      <Box
                        component="span"
                        sx={{
                          color: corSaldo(linha.saldo),
                          fontWeight: "bold",
                        }}
                      >
                        {formatarValor(linha.saldo)}
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {/* Procedimentos plano */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <LocalHospitalIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Procedimentos de Plano (
                        {linha.entradas.procedimentosPlano.numSessoes} sessões,{" "}
                        ~{linha.entradas.procedimentosPlano.numProcedimentos}{" "}
                        procedimentos)
                      </Typography>
                    </Box>
                    {det.procedimentosPlano.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell>Dente/Faces</TableCell>
                              <TableCell align="right">Sessões</TableCell>
                              <TableCell align="right">Preço</TableCell>
                              <TableCell align="right">Atribuído</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {det.procedimentosPlano.map((d, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  {moment(d.dataSessao).format("DD/MM/YY")}
                                </TableCell>
                                <TableCell>
                                  {d.dente && `D: ${d.dente}`}
                                  {d.faces && d.faces.length > 0 && (
                                    <> | F: {d.faces.join(",")}</>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {d.totalSessoes}
                                </TableCell>
                                <TableCell align="right">
                                  {formatarValor(d.precoSegurado)}
                                </TableCell>
                                <TableCell align="right">
                                  <strong>{formatarValor(d.valor)}</strong>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Nenhum procedimento neste período.
                      </Typography>
                    )}
                  </Grid>

                  {/* Tratamentos isolados */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <MedicalServicesIcon color="secondary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Tratamentos Isolados (
                        {linha.entradas.tratamentosIsolados.numSessoes} sessões)
                      </Typography>
                    </Box>
                    {det.tratamentosIsolados.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell>Dente/Faces</TableCell>
                              <TableCell>Tipo</TableCell>
                              <TableCell align="right">Atribuído</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {det.tratamentosIsolados.map((d, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  {moment(d.dataSessao).format("DD/MM/YY")}
                                </TableCell>
                                <TableCell>
                                  {d.dente && `D: ${d.dente}`}
                                  {d.faces && d.faces.length > 0 && (
                                    <> | F: {d.faces.join(",")}</>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={
                                      d.ehGrupoSessao
                                        ? `Sessão (1/${d.totalSessoesGrupo})`
                                        : "Completo"
                                    }
                                    size="small"
                                    color={
                                      d.ehGrupoSessao ? "warning" : "success"
                                    }
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <strong>{formatarValor(d.valor)}</strong>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Nenhum tratamento isolado neste período.
                      </Typography>
                    )}
                  </Grid>

                  {/* Despesas */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <ReceiptIcon color="error" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Despesas ({linha.gastos.despesas.numItens})
                      </Typography>
                    </Box>
                    {det.despesas.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell>Descrição</TableCell>
                              <TableCell>Método</TableCell>
                              <TableCell align="right">Valor</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {det.despesas.map((d, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  {d.dataPagamento
                                    ? moment(d.dataPagamento).format("DD/MM/YY")
                                    : "-"}
                                </TableCell>
                                <TableCell>
                                  {d.descricao || d.observacoes || "-"}
                                </TableCell>
                                <TableCell>
                                  {d.metodoPagamento || "-"}
                                </TableCell>
                                <TableCell align="right">
                                  <strong>{formatarValor(d.valor)}</strong>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Nenhuma despesa neste período.
                      </Typography>
                    )}
                  </Grid>

                  {/* Consumo Stock */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <Inventory2Icon color="warning" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Consumo de Stock (
                        {linha.gastos.consumoStock.numMovimentos} movimentos,{" "}
                        {linha.gastos.consumoStock.quantidadeTotal} unidades)
                      </Typography>
                    </Box>
                    {det.consumoStock.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell>Lote</TableCell>
                              <TableCell align="right">Qtd</TableCell>
                              <TableCell align="right">Custo Unit.</TableCell>
                              <TableCell align="right">Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {det.consumoStock.map((d, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  {d.data
                                    ? moment(d.data).format("DD/MM/YY")
                                    : "-"}
                                </TableCell>
                                <TableCell>{d.lote || "-"}</TableCell>
                                <TableCell align="right">
                                  {d.quantidade}
                                </TableCell>
                                <TableCell align="right">
                                  {formatarValor(d.precoCompra)}
                                </TableCell>
                                <TableCell align="right">
                                  <strong>{formatarValor(d.valor)}</strong>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Nenhum consumo neste período.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Box>
  );
};

export default RelatorioContabilidadeAnalitica;
